import React, { useCallback, useDeferredValue, useMemo, useState } from "react";
import { Category, Locale } from "../../model/Category";
import { ChevronRight, ChevronDown, Edit, Search, Trash2 } from "lucide-react";

interface ListCategoryItemsProps {
  data: Category[];
  handleManageItem: (category: Category) => void;
  handleDeleteItem: (category: Category) => void;
  deletingIds?: Set<number>;
}

const normalize = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const displayNameOf = (category: Category): string => {
  const names = category.display_name ?? {};
  return names[Locale.PT] ?? names[Locale.PT.toLowerCase()] ?? "";
};

const matchesQuery = (category: Category, query: string): boolean =>
  normalize(category.name).includes(query) ||
  normalize(category.slug ?? "").includes(query) ||
  normalize(displayNameOf(category)).includes(query);

const filterTree = (nodes: Category[], query: string): Category[] => {
  if (!query) return nodes;
  const result: Category[] = [];
  for (const node of nodes) {
    if (matchesQuery(node, query)) {
      // A matching parent brings its whole subtree with it.
      result.push(node);
      continue;
    }
    const children = filterTree(node.children ?? [], query);
    if (children.length > 0) {
      result.push({ ...node, children });
    }
  }
  return result;
};

const collectParentIds = (
  nodes: Category[],
  acc = new Set<number>(),
): Set<number> => {
  for (const node of nodes) {
    if (node.children?.length) {
      if (node.id != null) acc.add(node.id);
      collectParentIds(node.children, acc);
    }
  }
  return acc;
};

const countNodes = (nodes: Category[]): number =>
  nodes.reduce((total, node) => total + 1 + countNodes(node.children ?? []), 0);

const EMPTY_IDS = new Set<number>();

interface CategoryRowProps {
  category: Category;
  level: number;
  collapsedIds: Set<number>;
  toggleDisabled: boolean;
  deletingIds: Set<number>;
  onToggle: (id: number) => void;
  onManage: (category: Category) => void;
  onDelete: (category: Category) => void;
}

const areCategoryRowsEqual = (
  prev: CategoryRowProps,
  next: CategoryRowProps,
): boolean => {
  if (prev.category !== next.category) return false;
  if (prev.level !== next.level) return false;
  if (prev.toggleDisabled !== next.toggleDisabled) return false;
  if (prev.deletingIds !== next.deletingIds) return false;
  if (prev.collapsedIds !== next.collapsedIds) return false;
  if (prev.onToggle !== next.onToggle) return false;
  if (prev.onManage !== next.onManage) return false;
  if (prev.onDelete !== next.onDelete) return false;
  return true;
};

const CategoryRow = React.memo(function CategoryRow({
  category,
  level,
  collapsedIds,
  toggleDisabled,
  deletingIds,
  onToggle,
  onManage,
  onDelete,
}: CategoryRowProps) {
  const hasChildren = !!category.children?.length;
  const canToggle = hasChildren && category.id != null;
  const expanded = canToggle && !collapsedIds.has(category.id!);
  const deleteDisabled = category.id != null && deletingIds.has(category.id);

  return (
    <React.Fragment>
      <tr className="hover:bg-gray-50 transition border-b border-gray-100 last:border-0">
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          <div
            className="flex items-center gap-2"
            style={{ paddingLeft: `${level * 20}px` }}
          >
            {canToggle ? (
              <button
                type="button"
                onClick={() => onToggle(category.id!)}
                disabled={toggleDisabled}
                aria-expanded={expanded}
                aria-label={`${expanded ? "Recolher" : "Expandir"} ${category.name}`}
                className="text-gray-500 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {expanded ? (
                  <ChevronDown size={16} />
                ) : (
                  <ChevronRight size={16} />
                )}
              </button>
            ) : (
              <div className="w-4" aria-hidden="true" />
            )}
            <span className="font-medium">{category.name}</span>
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {displayNameOf(category) || <span className="text-gray-300">—</span>}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {category.slug}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {category.order}
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              category.status === "PUBLISHED"
                ? "bg-green-100 text-green-800"
                : "bg-yellow-100 text-yellow-800"
            }`}
          >
            {category.status}
          </span>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={() => onManage(category)}
              className="text-blue-600 hover:text-blue-900 p-1.5 hover:bg-blue-50 rounded transition focus-visible:ring-2 focus-visible:ring-blue-500"
              title="Editar"
              aria-label={`Editar ${category.name}`}
            >
              <Edit className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => onDelete(category)}
              disabled={deleteDisabled}
              className="text-red-600 hover:text-red-900 p-1.5 hover:bg-red-50 rounded transition focus-visible:ring-2 focus-visible:ring-red-500 disabled:opacity-40 disabled:cursor-not-allowed"
              title="Apagar"
              aria-label={`Apagar ${category.name}`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </td>
      </tr>
      {expanded &&
        category.children!.map((child) => (
          <CategoryRow
            key={child.id}
            category={child}
            level={level + 1}
            collapsedIds={collapsedIds}
            toggleDisabled={toggleDisabled}
            deletingIds={deletingIds}
            onToggle={onToggle}
            onManage={onManage}
            onDelete={onDelete}
          />
        ))}
    </React.Fragment>
  );
}, areCategoryRowsEqual);

const ListCategoryItems: React.FC<ListCategoryItemsProps> = ({
  data,
  handleManageItem,
  handleDeleteItem,
  deletingIds = EMPTY_IDS,
}) => {
  const [query, setQuery] = useState("");
  const [collapsedIds, setCollapsedIds] = useState<Set<number>>(
    () => new Set(),
  );

  const deferredQuery = useDeferredValue(query);
  const normalizedQuery = normalize(deferredQuery);
  const isSearching = normalize(query).length > 0;
  const visibleData = useMemo(
    () => filterTree(data, normalizedQuery),
    [data, normalizedQuery],
  );
  const visibleCount = useMemo(() => countNodes(visibleData), [visibleData]);
  const totalCount = useMemo(() => countNodes(data), [data]);
  const effectiveCollapsedIds = isSearching ? EMPTY_IDS : collapsedIds;

  const toggle = useCallback((id: number) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const expandAll = useCallback(() => setCollapsedIds(new Set()), []);
  const collapseAll = useCallback(
    () => setCollapsedIds(collectParentIds(data)),
    [data],
  );

  if (data.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
        <p className="text-gray-500">Nenhuma categoria encontrada.</p>
        <p className="text-sm text-gray-400 mt-2">
          Clique em "Nova Categoria" para adicionar uma nova.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="flex flex-col gap-3 p-4 border-b border-gray-200 md:flex-row md:items-center md:justify-between">
        <div className="relative w-full md:max-w-sm">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
            aria-hidden="true"
          />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Pesquisar por nome ou slug"
            aria-label="Pesquisar categorias"
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-gray-500 mr-auto md:mr-0">
            A mostrar {visibleCount} de {totalCount} categoria
            {totalCount === 1 ? "" : "s"}
          </span>
          <button
            type="button"
            onClick={expandAll}
            disabled={isSearching}
            className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Expandir tudo
          </button>
          <button
            type="button"
            onClick={collapseAll}
            disabled={isSearching}
            className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Recolher tudo
          </button>
        </div>
      </div>
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Nome
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Nome (loja)
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Slug
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Ordem
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Estado
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Ações
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {visibleData.length === 0 ? (
            <tr>
              <td
                colSpan={6}
                className="px-6 py-12 text-center text-sm text-gray-500"
              >
                Sem categorias para "{query}".
              </td>
            </tr>
          ) : (
            visibleData.map((category) => (
              <CategoryRow
                key={category.id}
                category={category}
                level={0}
                collapsedIds={effectiveCollapsedIds}
                toggleDisabled={isSearching}
                deletingIds={deletingIds}
                onToggle={toggle}
                onManage={handleManageItem}
                onDelete={handleDeleteItem}
              />
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default ListCategoryItems;
